import { Fragment, useEffect, useMemo, useRef } from 'react';

import { Box } from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useHistory, useParams } from 'react-router-dom';

import { MobileMenu } from './components/MobileMenu';
import { Navbar } from './components/Navbar';
import { RotatingSponsorBanner } from './components/RotatingSponsorBanner';
import { SponsorTicker } from './components/SponsorTicker';
import { sections } from './constants';
import {
  opening as crOpening,
  rankings as crRankings,
  stock as crStock,
} from './data/cr';
import {
  opening as fiagroOpening,
  rankings as fiagroRankings,
  stock as fiagroStock,
} from './data/fiagro';
import {
  opening as fidcOpening,
  rankings as fidcRankings,
  sponsorArticles as fidcSponsorArticles,
  stock as fidcStock,
} from './data/fidc';
import {
  opening as fiiOpening,
  rankings as fiiRankings,
  stock as fiiStock,
} from './data/fii';
import { greetings } from './data/greetings';
import { letterToReaderParagraphs } from './data/letterToReader';
import { marketVideos } from './data/videos';
import { getMarketData, MarketKey } from './market';
import { HeroSection } from './sections/Hero';
import { LetterToReader } from './sections/LetterToReader';
import { OpeningSection } from './sections/Opening';
import { RankingsSection } from './sections/Rankings';
import { SponsorArticles } from './sections/SponsorArticles';
import { Sponsors } from './sections/Sponsors';
import { StockSection } from './sections/Stock';
import { VideosSection } from './sections/Videos';
import { getSubsectionComponent } from './sections/registry';

export interface YearbookParams {
  year: string;
  market?: string;
  section?: string;
}

const safeMarginTop = 140;

const sponsorArticlesByMarket: Partial<
  Record<MarketKey, typeof fidcSponsorArticles>
> = {
  fidc: fidcSponsorArticles,
};

const openingByMarket: Partial<Record<MarketKey, typeof fidcOpening>> = {
  crs: crOpening,
  fidc: fidcOpening,
  fiagro: fiagroOpening,
  fii: fiiOpening,
};

const stockByMarket: Partial<
  Record<
    MarketKey,
    typeof fidcStock | typeof crStock | typeof fiagroStock | typeof fiiStock
  >
> = {
  crs: crStock,
  fidc: fidcStock,
  fiagro: fiagroStock,
  fii: fiiStock,
};

const rankingsByMarket: Partial<
  Record<
    MarketKey,
    | typeof fidcRankings
    | typeof crRankings
    | typeof fiagroRankings
    | typeof fiiRankings
  >
> = {
  crs: crRankings,
  fidc: fidcRankings,
  fiagro: fiagroRankings,
  fii: fiiRankings,
};

function resolveSectionId(sectionParam: string): string {
  const match = sections.find(
    (s) => s.title === sectionParam || sectionParam.startsWith(`${s.title} `),
  );
  return match?.title ?? sectionParam;
}

type MainSectionRenderer = (params: {
  marketParam?: string;
  sectionId: string;
}) => JSX.Element | null;

const mainSectionRenderers: Record<string, MainSectionRenderer> = {
  Patrocinadores: ({ sectionId }) => <Sponsors id={sectionId} />,
  'Carta ao leitor': ({ sectionId }) => {
    if (!letterToReaderParagraphs.length) {
      return <Box component="section" id={sectionId} data-info="no-content" />;
    }

    return (
      <LetterToReader id={sectionId} paragraphs={letterToReaderParagraphs} />
    );
  },
  Abertura: ({ marketParam, sectionId }) => {
    const opening = getMarketData(marketParam, openingByMarket) ?? [];

    if (!opening.length) {
      return <Box component="section" id={sectionId} data-info="no-content" />;
    }

    return <OpeningSection id={sectionId} paragraphs={opening} />;
  },
  Artigos: ({ marketParam, sectionId }) => {
    const articles = getMarketData(marketParam, sponsorArticlesByMarket) ?? [];

    if (!articles.length) {
      return <Box component="section" id={sectionId} data-info="no-content" />;
    }

    return <SponsorArticles id={sectionId} articles={articles} />;
  },
  Estoque: ({ marketParam, sectionId }) => {
    const stock = getMarketData(marketParam, stockByMarket);

    if (!stock) {
      return <Box component="section" id={sectionId} data-info="no-content" />;
    }

    return <StockSection id={sectionId} data={stock} />;
  },
  Vídeos: ({ sectionId }) => {
    if (!marketVideos.length) {
      return <Box component="section" id={sectionId} data-info="no-content" />;
    }

    return <VideosSection id={sectionId} videos={marketVideos} />;
  },
  Rankings: ({ marketParam, sectionId }) => {
    const rankings = getMarketData(marketParam, rankingsByMarket);

    if (!rankings) {
      return <Box component="section" id={sectionId} data-info="no-content" />;
    }

    return <RankingsSection id={sectionId} data={rankings} />;
  },
};

export function Yearbooks2026({ paywallBlur }: { paywallBlur: boolean }) {
  const { year, market, section } = useParams<YearbookParams>();
  const history = useHistory();
  const skipScrollEffectRef = useRef(false);
  const previousMarketRef = useRef<string | undefined>(market);

  const availableSectionIds = useMemo(() => {
    const mappedSections = sections.flatMap((s) => {
      if (!s.subsections) {
        return [kebabcase(s.title)];
      }

      return s.subsections.map((sub) => kebabcase(`${s.title} ${sub.title}`));
    });

    return ['inicio', ...mappedSections];
  }, []);

  function hasSectionContent(content: JSX.Element) {
    return content.props?.['data-info'] !== 'no-content';
  }

  useEffect(() => {
    const id = section ? resolveSectionId(section) : 'inicio';
    const el = document.getElementById(id);
    previousMarketRef.current = market;

    if (skipScrollEffectRef.current) {
      skipScrollEffectRef.current = false;
      return;
    }

    if (el) {
      const top =
        el.getBoundingClientRect().top + window.scrollY - safeMarginTop;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [market, section]);

  useEffect(() => {
    if (!year || !market) {
      return;
    }

    const handleScroll = () => {
      const marker = window.scrollY + safeMarginTop + 8;

      let currentSection = 'inicio';

      availableSectionIds.forEach((id) => {
        const element = document.getElementById(id);

        if (!element) {
          return;
        }

        if (element.offsetTop <= marker) {
          currentSection = id;
        }
      });

      if (section === currentSection) {
        return;
      }

      skipScrollEffectRef.current = true;
      history.replace(`/anuarios/${year}/${market}/${currentSection}`);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [availableSectionIds, history, market, section, year]);

  return (
    <Box
      sx={{
        backgroundColor: '#0E0F0E',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        mx: 'auto',
        position: 'relative',
      }}>
      <Navbar />
      <MobileMenu />
      <SponsorTicker triggerSectionId="carta-ao-leitor" />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          backgroundColor: '#0E0F0E',
          maxWidth: { xs: '100%', md: theme.breakpoints.values.lg },
          p: { xs: 2, md: 4 },
          zIndex: 10,
        })}>
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          px={{ xs: 1, md: 3 }}>
          <HeroSection id="inicio" greetings={greetings} />

          {sections.map((s) => {
            const sectionId = kebabcase(s.title);
            const renderer = mainSectionRenderers[s.title];

            if (renderer) {
              const content = renderer({
                marketParam: market,
                sectionId,
              });

              if (!content) {
                return null;
              }

              const isContentAvailable = hasSectionContent(content);

              if (s.title === 'Abertura') {
                return (
                  <Fragment key={sectionId}>
                    {content}
                    {isContentAvailable && (
                      <RotatingSponsorBanner
                        sectionKey={`${sectionId}-after`}
                      />
                    )}
                  </Fragment>
                );
              }

              if (s.title === 'Vídeos') {
                return (
                  <Fragment key={sectionId}>
                    {isContentAvailable && (
                      <RotatingSponsorBanner
                        sectionKey={`${sectionId}-before`}
                      />
                    )}
                    {content}
                  </Fragment>
                );
              }

              return <Box key={sectionId}>{content}</Box>;
            }

            if (s.subsections) {
              return s.subsections.map((sub) => {
                const sectionId = kebabcase(`${s.title} ${sub.title}`);
                const Component = getSubsectionComponent(
                  market,
                  s.title,
                  sub.title,
                );

                if (Component) {
                  return <Component key={sectionId} id={sectionId} />;
                }

                return (
                  <Box
                    key={sectionId}
                    component="section"
                    id={sectionId}
                    data-info="no-content"
                  />
                );
              });
            }

            return (
              <Box
                key={s.title}
                id={kebabcase(s.title)}
                width="100%"
                height={400}
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="1px solid #FF8211"
                borderRadius="24px">
                {s.title}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
